import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, ShieldCheck, Truck } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { whatsappLink, BRAND } from "../mock/mock";
import { WhatsAppIcon } from "../components/WhatsAppFloat";

const buildWhatsAppOrder = (items, totals) => {
  const lines = [
    `Hello Agrawal Rich Dry Fruits,`,
    ``,
    `I'd like to place an order:`,
    ``,
    ...items.map((it, i) => `${i + 1}. ${it.name} — ${it.weight} × ${it.qty} = ₹${(it.price * it.qty).toLocaleString("en-IN")}`),
    ``,
    `Subtotal: ₹${totals.subtotal.toLocaleString("en-IN")}`,
    totals.savings > 0 ? `You save: ₹${totals.savings.toLocaleString("en-IN")}` : "",
    `Shipping: ${totals.shipping === 0 ? "FREE" : `₹${totals.shipping}`}`,
    `Total: ₹${totals.total.toLocaleString("en-IN")}`,
    ``,
    `Please confirm availability and delivery details. Thank you!`,
  ].filter(Boolean).join("\n");
  return whatsappLink(lines);
};

const CartPage = () => {
  const { items, updateQty, removeItem, totals, clear } = useCart();
  const { toast } = useToast();
  const nav = useNavigate();

  const checkout = () => {
    if (items.length === 0) return;
    const url = buildWhatsAppOrder(items, totals);
    window.open(url, "_blank", "noopener,noreferrer");
    toast({ title: "Redirecting to WhatsApp", description: "Your order details have been prepared. Please send the message to place your order." });
  };

  return (
    <main className="bg-cream min-h-screen">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-8">
        <h1 className="font-serif-brand text-[32px] md:text-[42px] font-bold text-[#2a1a15]">Your Cart</h1>
        <p className="text-[14px] text-[#6b4f39] mt-1">{totals.count} item(s) · Order fulfilled via WhatsApp — personal, fast and reliable.</p>

        {items.length === 0 ? (
          <div className="mt-10 bg-white rounded-2xl border border-[#efe4c9] p-12 text-center">
            <ShoppingBag className="h-14 w-14 mx-auto text-brand/60" />
            <div className="font-serif-brand text-2xl text-[#2a1a15] mt-3">Your cart is empty</div>
            <p className="text-[#6b4f39] mt-1.5 text-sm">Discover our premium dry fruits and start filling it with goodness.</p>
            <Link to="/shop" className="mt-5 inline-flex items-center gap-2 bg-brand hover:bg-[#6c1414] text-white font-semibold px-6 py-3 rounded-full transition-colors">Continue Shopping <ArrowRight className="h-4 w-4" /></Link>
          </div>
        ) : (
          <div className="mt-6 grid lg:grid-cols-[1fr_380px] gap-6">
            <div className="space-y-3">
              {items.map((it) => (
                <div key={it.id} className="flex gap-4 bg-white rounded-2xl border border-[#efe4c9] p-3 md:p-4">
                  <Link to={`/product/${it.id}`} className="h-24 w-24 md:h-28 md:w-28 rounded-xl overflow-hidden bg-[#f9f1de] shrink-0">
                    <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-[0.2em] font-bold text-olive">AGRAWAL RICH</div>
                    <Link to={`/product/${it.id}`} className="font-medium text-[14.5px] text-[#2a1a15] leading-snug line-clamp-2 hover:text-brand">{it.name}</Link>
                    <div className="text-[12.5px] text-[#6b4f39]">{it.weight}</div>
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <div className="flex items-center bg-cream border border-[#e3d3ad] rounded-full">
                        <button onClick={() => updateQty(it.id, it.qty - 1)} className="h-9 w-9 flex items-center justify-center hover:bg-[#f3ecda] rounded-l-full"><Minus className="h-3.5 w-3.5" /></button>
                        <div className="w-9 text-center text-sm font-semibold">{it.qty}</div>
                        <button onClick={() => updateQty(it.id, it.qty + 1)} className="h-9 w-9 flex items-center justify-center hover:bg-[#f3ecda] rounded-r-full"><Plus className="h-3.5 w-3.5" /></button>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-[#2a1a15]">₹{(it.price * it.qty).toLocaleString("en-IN")}</div>
                        {it.mrp > it.price && <div className="text-[12px] line-through text-[#a08774]">₹{(it.mrp * it.qty).toLocaleString("en-IN")}</div>}
                      </div>
                      <button onClick={() => removeItem(it.id)} className="text-[#a08774] hover:text-brand p-1.5"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
              <button onClick={clear} className="text-[13px] text-[#6b4f39] hover:text-brand underline">Clear cart</button>
            </div>

            <div className="bg-white rounded-2xl border border-[#efe4c9] p-5 md:p-6 h-fit">
              <div className="font-serif-brand text-xl font-bold text-[#2a1a15]">Order Summary</div>
              <div className="mt-4 space-y-2.5 text-[14px]">
                <div className="flex justify-between"><span className="text-[#6b4f39]">Subtotal</span><span>₹{totals.subtotal.toLocaleString("en-IN")}</span></div>
                {totals.savings > 0 && <div className="flex justify-between text-olive"><span>You save</span><span>– ₹{totals.savings.toLocaleString("en-IN")}</span></div>}
                <div className="flex justify-between"><span className="text-[#6b4f39]">Shipping</span><span>{totals.shipping === 0 ? "FREE" : `₹${totals.shipping}`}</span></div>
                <div className="border-t border-[#efe4c9] pt-3 flex justify-between font-serif-brand text-lg font-bold text-[#2a1a15]"><span>Total</span><span>₹{totals.total.toLocaleString("en-IN")}</span></div>
              </div>
              {totals.subtotal < 1499 && (
                <div className="mt-3 text-[12.5px] text-[#6b4f39] bg-[#f9f1de] rounded-lg p-2.5">Add ₹{(1499 - totals.subtotal).toLocaleString("en-IN")} more for FREE shipping.</div>
              )}
              <button onClick={checkout} className="mt-5 w-full h-12 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white font-semibold transition-colors flex items-center justify-center gap-2"><WhatsAppIcon className="h-5 w-5" /> Place Order on WhatsApp</button>
              <p className="mt-2.5 text-[11.5px] text-[#6b4f39] text-center">Your order details are auto-filled in WhatsApp — just hit send. Or call <a href={`tel:${BRAND.phoneRaw}`} className="font-semibold text-brand">{BRAND.phone}</a>.</p>
              <div className="mt-4 grid grid-cols-2 gap-2 text-[12px] text-[#6b4f39]">
                <div className="flex items-center gap-1.5"><Truck className="h-4 w-4 text-olive" /> Fast shipping</div>
                <div className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-olive" /> Quality assured</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default CartPage;
